<script lang="ts" setup>
import { analytics, fileExports } from '@thecompaniesapi/shared/client'

const { getMetaAnalyticsAttributes, getQueryAttributes } = useApiAttributes()
const { refreshResponseWithApiVersion } = useApiDoc()
const { t } = useI18n()
const api = useApi()

const endpoints: ApiEndpoint[] = [
  {
    method: 'GET',
    path: '/companies/analytics',
  },
]

const queryAttributes: ApiAttribute[] = [
  {
    description: t('attributes.attribute'),
    name: 'attribute',
    required: true,
    type: 'string',
    values: analytics.analyticsAttributes as any,
  },
  {
    attributes: getQueryAttributes(),
    description: t('attributes.query'),
    endpoint: '/companies/analytics',
    hasSegmentation: true,
    name: 'query',
    optional: true,
    type: 'array',
    warning: t('globals.stringifyAndEncode'),
  },
  {
    description: t('attributes.listIds'),
    name: 'listIds',
    optional: true,
    type: 'array',
  },
  {
    description: t('attributes.size'),
    name: 'size',
    optional: true,
    type: 'number',
    values: ['1-10000'],
  },
  {
    description: t('attributes.sort'),
    name: 'sort',
    optional: true,
    type: 'string',
    values: ['asc', 'desc'],
  },
]

const exportAttributes: ApiAttribute[] = [
  {
    description: t('attributes.attributes'),
    name: 'attributes',
    optional: true,
    type: 'array',
    values: analytics.analyticsAttributes as any,
  },
  {
    description: t('attributes.full'),
    name: 'full',
    optional: true,
    type: 'boolean',
  },
  {
    description: t('attributes.format'),
    name: 'format',
    optional: true,
    type: 'string',
    values: fileExports.formats,
  },
]

const responseAttributes: ApiAttribute[] = [
  {
    attributes: [
      {
        description: t('response.count'),
        name: 'count',
        type: 'number',
      },
      {
        description: t('response.name'),
        name: 'name',
        type: 'string',
      },
      {
        description: t('response.percentageOfAll'),
        name: 'percentageOfAll',
        type: 'number',
      },
      {
        description: t('response.percentageOfTotal'),
        name: 'percentageOfTotal',
        type: 'number',
      },
    ],
    description: t('response.data'),
    name: 'data',
    type: 'array',
  },
  getMetaAnalyticsAttributes(),
]

async function onVisibilityChange(visibility: boolean) {
  refreshResponseWithApiVersion({
    api,
    execute,
    response,
    visibility,
  })
}

const { data: response, execute, status } = useAsyncData(
  'doc:companies-analytics',
  () => api.companies.analytics.fetch({
    attribute: 'about.businessType',
    size: 50,
  }),
  { immediate: false },
)
</script>

<template>
  <ApiDoc
    :endpoints="endpoints"
    :export-attributes="exportAttributes"
    :loading="status === 'pending'"
    :query-attributes="queryAttributes"
    :response="response?.data"
    :response-attributes="responseAttributes"
    @visibility-change="onVisibilityChange"
  />
</template>

<i18n lang="json">
{
  "en": {
    "attributes": {
      "attribute": "The analytics attribute to aggregate data for. This determines which field will be used to group and analyze the companies.",
      "attributes": "An array of analytics attributes to export. Required if full is not true.",
      "format": "The format of the exported file.",
      "full": "Whether to export all analytics attributes.",
      "listIds": "Filter analytics to specific lists.",
      "query": "An array of conditions to filter the companies.",
      "size": "The maximum number of companies to include in the analysis (between 1 and 10,000). The default value is 100.",
      "sort": "The sort order of the results."
    },
    "response": {
      "count": "The number of companies with this value.",
      "data": "Array of aggregated metrics (counts and percentages) for each value of the specified attribute.",
      "name": "The value of the attribute.",
      "percentageOfAll": "Percentage relative to all companies in the database.",
      "percentageOfTotal": "Percentage relative to the filtered results."
    }
  },
  "es": {
    "attributes": {
      "attribute": "El atributo de análisis para agregar datos. Determina qué campo se utilizará para agrupar y analizar las empresas.",
      "attributes": "Una matriz de atributos de análisis para exportar. Requerido si full no es verdadero.",
      "format": "El formato del archivo exportado.",
      "full": "Si se deben exportar todos los atributos de análisis.",
      "listIds": "Filtrar análisis a listas específicas.",
      "query": "Una matriz de condiciones para filtrar las empresas.",
      "size": "El número máximo de empresas a incluir en el análisis (entre 1 y 10,000). El valor por defecto es 100.",
      "sort": "El orden de clasificación de los resultados."
    },
    "response": {
      "count": "El número de empresas con este valor.",
      "data": "Matriz de métricas agregadas (recuentos y porcentajes) para cada valor del atributo especificado.",
      "name": "El valor del atributo.",
      "percentageOfAll": "Porcentaje relativo a todas las empresas en la base de datos.",
      "percentageOfTotal": "Porcentaje relativo a los resultados filtrados."
    }
  },
  "fr": {
    "attributes": {
      "attribute": "L'attribut d'analyse pour agréger les données. Détermine quelle colonne sera utilisée pour regrouper et analyser les entreprises.",
      "attributes": "Un tableau d'attributs d'analyse à exporter. Requis si full n'est pas vrai.",
      "format": "Le format du fichier exporté.",
      "full": "Si tous les attributs d'analyse doivent être exportés.",
      "listIds": "Filtrer les analyses pour des listes spécifiques.",
      "query": "Un tableau de conditions pour filtrer les entreprises.",
      "size": "Le nombre maximum d'entreprises à inclure dans l'analyse (entre 1 et 10,000). La valeur par défaut est 100.",
      "sort": "L'ordre de tri des résultats."
    },
    "response": {
      "count": "Le nombre d'entreprises avec cette valeur.",
      "data": "Tableau de métriques agrégées (nombres et pourcentages) pour chaque valeur de l'attribut spécifié.",
      "name": "La valeur de l'attribut.",
      "percentageOfAll": "Pourcentage par rapport à toutes les entreprises de la base de données.",
      "percentageOfTotal": "Pourcentage par rapport aux résultats filtrés."
    }
  }
}
</i18n>
